import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { TeamRole } from "@/hooks/useTeam";

export interface Permissions {
  role: TeamRole | null;
  isLoading: boolean;
  // Navigation access
  canAccessFinance: boolean;   // Faturas, Despesas, Financeiro
  canAccessSettings: boolean;  // Definições (full)
  canAccessSubscription: boolean;
  canAccessFornecedores: boolean;
  canAccessObras: boolean;     // Projetos (Business only, admin + tecnico)
  canAccessRelatorios: boolean; // Relatórios (Business only, admin)
  // CRUD
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  // Team management
  canManageTeam: boolean;
}

export function usePermissions(): Permissions {
  const { user } = useAuth();

  const { data: role, isLoading } = useQuery({
    queryKey: ["user-role", user?.id],
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("role")
        .eq("user_id", user!.id)
        .eq("is_active", true)
        .single();
      if (error) return "admin" as TeamRole; // fallback for solo users
      return data.role as TeamRole;
    },
  });

  const r = role || null;

  return {
    role: r,
    isLoading,
    canAccessFinance: r === "admin",
    canAccessSettings: r === "admin",
    canAccessSubscription: r === "admin",
    canAccessFornecedores: r === "admin" || r === "tecnico",
    canAccessObras: r === "admin" || r === "tecnico",
    canAccessRelatorios: r === "admin",
    canCreate: r === "admin" || r === "tecnico",
    canEdit: r === "admin" || r === "tecnico",
    canDelete: r === "admin" || r === "tecnico",
    canManageTeam: r === "admin",
  };
}
